import { Injectable } from '@angular/core';
import { HttpParams, HttpClient } from '@angular/common/http';
import { Router, NavigationEnd } from '@angular/router';
import { BehaviorSubject, Observable } from 'rxjs';
import { filter } from 'rxjs/operators';
import { CalendarWeeklyData } from '@sd-portal/pre-flight/feature-calendar';

export type AppSearchContext = 'pre-flight' | 'post-flight';

export interface FullTextTripSearchResultItemICAO {
  scheduledFlightId?: number;
  postedFlightId?: number;
  departureIcao: string;
  arrivalIcao: string;
  startDate: string;
  endDate: string;
}

export interface FullTextTripSearchResultItem {
  scheduledAircraftTripId?: number;
  customTripId: string;
  icaos?: FullTextTripSearchResultItemICAO[];
}

@Injectable({ providedIn: 'root' })
export class AppSearchService {
  private readonly baseUrl: string = '/sdapi/pre-flight/calendar/';
  private readonly showSearch = new BehaviorSubject<boolean>(false);
  private readonly showSearchFromDraftTrip = new BehaviorSubject<boolean>(false);
  private readonly searchContext = new BehaviorSubject<AppSearchContext>('pre-flight');

  showSearch$ = this.showSearch.asObservable();
  showSearchFromDraftTrip$ = this.showSearchFromDraftTrip.asObservable();

  constructor(
    private readonly http: HttpClient,
    private readonly router: Router
  ) {
    this.updateSearchContext(this.router.url);

    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        this.updateSearchContext(event.urlAfterRedirects);
      });
  }

  setShowSearch(show: boolean) {
    this.showSearch.next(show);
  }

  setShowSearchFromDraftTrip(show: boolean) {
    this.showSearchFromDraftTrip.next(show);
  }

  private updateSearchContext(url: string): void {
    let context: AppSearchContext = 'pre-flight';

    if (url.includes('/post-flight')) {
      context = 'post-flight';
    } else if (url.includes('/pre-flight')) {
      context = 'pre-flight';
    }

    this.searchContext.next(context);
  }

  getSearchContext(): AppSearchContext {
    return this.searchContext.value;
  }

  /**
   * Pre-flight calendar trip search.
   */
  getCalendarTripSearch(
    searchText: string,
    startDateTime: string,
    endDateTime: string
  ): Observable<CalendarWeeklyData[]> {
    const url = `${this.baseUrl}TripSearch`;
    let params = new HttpParams();
    params = params.set('searchText', searchText);
    if (startDateTime) {
      params = params.set('startDateTime', startDateTime);
    }
    if (endDateTime) {
      params = params.set('endDateTime', endDateTime);
    }

    params = params.set('showDraftTripsOnly', this.showSearchFromDraftTrip.getValue().toString());

    return this.http.get<CalendarWeeklyData[]>(url, { params });
  }

  /**
   * Full-text trip search (supports both pre-flight and post-flight results).
   */
  getFullTextTripSearch(
    searchText: string,
    startDateTime: string,
    endDateTime: string
  ): Observable<FullTextTripSearchResultItem[]> {
    const url = '/sdapi/search/TripSearch/FullText';
    let params = new HttpParams();
    params = params.set('searchText', searchText);
    if (startDateTime) {
      params = params.set('startDateTime', startDateTime);
    }
    if (endDateTime) {
      params = params.set('endDateTime', endDateTime);
    }

    return this.http.get<FullTextTripSearchResultItem[]>(url, { params });
  }
}
